/**
 * Leitor de XML mínimo e estrito, sem dependência de DOM (roda no navegador e no Node).
 * Suficiente para NF-e: elementos, atributos, texto, comentários, CDATA e entidades.
 * Prefixos de namespace são descartados (soap:Envelope → Envelope). XML mal formado lança erro.
 */

export type NoXml = {
  nome: string;
  attrs: Record<string, string>;
  filhos: NoXml[];
  texto: string;
};

const ENTIDADES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

function decodificar(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (m, ent: string) => {
    if (ent[0] === '#') {
      const cp = ent[1] === 'x' ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
      return Number.isFinite(cp) ? String.fromCodePoint(cp) : m;
    }
    return ENTIDADES[ent] ?? m;
  });
}

const semPrefixo = (nome: string) => nome.slice(nome.indexOf(':') + 1);

export function lerXml(fonte: string): NoXml {
  const texto = fonte.replace(/^\uFEFF/, '');
  const raiz: NoXml = { nome: '#documento', attrs: {}, filhos: [], texto: '' };
  const pilha: NoXml[] = [raiz];
  const rxTag = /<(\/?)([A-Za-z_][\w.:-]*)((?:\s+[\w.:-]+\s*=\s*(?:"[^"]*"|'[^']*'))*)\s*(\/?)>/y;
  const rxAttr = /([\w.:-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let i = 0;

  while (i < texto.length) {
    const lt = texto.indexOf('<', i);
    if (lt < 0) {
      if (texto.slice(i).trim()) pilha[pilha.length - 1].texto += decodificar(texto.slice(i));
      break;
    }
    if (lt > i) pilha[pilha.length - 1].texto += decodificar(texto.slice(i, lt));

    if (texto.startsWith('<?', lt)) {
      const fim = texto.indexOf('?>', lt);
      if (fim < 0) throw new Error('Instrução de processamento sem fechamento');
      i = fim + 2;
    } else if (texto.startsWith('<!--', lt)) {
      const fim = texto.indexOf('-->', lt);
      if (fim < 0) throw new Error('Comentário sem fechamento');
      i = fim + 3;
    } else if (texto.startsWith('<![CDATA[', lt)) {
      const fim = texto.indexOf(']]>', lt);
      if (fim < 0) throw new Error('CDATA sem fechamento');
      pilha[pilha.length - 1].texto += texto.slice(lt + 9, fim);
      i = fim + 3;
    } else if (texto.startsWith('<!', lt)) {
      const fim = texto.indexOf('>', lt);
      if (fim < 0) throw new Error('Declaração sem fechamento');
      i = fim + 1;
    } else {
      rxTag.lastIndex = lt;
      const m = rxTag.exec(texto);
      if (!m) throw new Error(`Tag mal formada na posição ${lt}`);
      const [bruto, fecha, nomeBruto, attrsBrutos, autoFecha] = m;
      const nome = semPrefixo(nomeBruto);
      if (fecha) {
        const topo = pilha.pop();
        if (!topo || topo === raiz || topo.nome !== nome) throw new Error(`Fechamento </${nomeBruto}> sem abertura correspondente`);
      } else {
        const attrs: Record<string, string> = {};
        for (const a of attrsBrutos.matchAll(rxAttr)) attrs[semPrefixo(a[1])] = decodificar(a[2] ?? a[3] ?? '');
        const no: NoXml = { nome, attrs, filhos: [], texto: '' };
        pilha[pilha.length - 1].filhos.push(no);
        if (!autoFecha) pilha.push(no);
      }
      i = lt + bruto.length;
    }
  }
  if (pilha.length !== 1) throw new Error(`Elemento <${pilha[pilha.length - 1].nome}> sem fechamento`);
  if (!raiz.filhos.length) throw new Error('Documento sem elemento raiz');
  return raiz;
}

/** Primeiro filho direto com o nome. */
export const filho = (no: NoXml | null | undefined, nome: string): NoXml | null => no?.filhos.find((f) => f.nome === nome) ?? null;

/** Todos os filhos diretos com o nome. */
export const filhos = (no: NoXml | null | undefined, nome: string): NoXml[] => no?.filhos.filter((f) => f.nome === nome) ?? [];

/** Texto (aparado) do filho direto; ausente ou vazio → null (campo ausente ≠ zero). */
export function valor(no: NoXml | null | undefined, nome: string): string | null {
  const t = filho(no, nome)?.texto.trim();
  return t ? t : null;
}

/** Busca em profundidade todos os elementos com o nome (qualquer nível). */
export function buscar(no: NoXml, nome: string, saida: NoXml[] = []): NoXml[] {
  for (const f of no.filhos) {
    if (f.nome === nome) saida.push(f);
    else buscar(f, nome, saida);
  }
  return saida;
}
